import React, { FC, useState, useEffect, ReactNode } from 'react';

import { mqMin } from '@lucid/theme';
import ReactModal from 'react-modal';
import { useTheme, Global } from '@emotion/react';

import { LucidTheme } from '../../types/types';
import Icon from '../icon/icon';
import Box from '../box/box';
import VideoPlayer from '../video/video-player/video-player';
import Button from '../button/button';

export interface ModalProps extends ReactModal.Props {
  /** The children to be shown when the modal is open. */
  children?: ReactNode;
  /** Allows the component to get additional styles. */
  className?: string;
  /** The link to the video. */
  videoLink?: string;
  /** The accessible description for the video content.  */
  videoDescription?: string;
  /** The ID of the element to contain the accessible video description. */
  videoDescriptionId?: string;
  /** The ReactPlayer config for the video. */
  videoConfig?: object;
  /** Whether the video should autoplay when the modal opens. */
  videoShouldAutoplay?: boolean;
}

const Modal: FC<ModalProps> = ({
  ariaHideApp = false,
  children,
  className,
  isOpen = false,
  style,
  onRequestClose,
  videoLink,
  videoDescription,
  videoDescriptionId,
  videoConfig,
  videoShouldAutoplay,
  ...rest
}) => {
  const [isModalOpen, setIsModalOpen] = useState(isOpen);
  const theme = useTheme();

  useEffect(() => {
    if (isOpen) {
      return setIsModalOpen(true);
    }

    return setIsModalOpen(false);
  }, [isOpen]);

  const handleOnClose = (e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>) => {
    if (onRequestClose) {
      onRequestClose(e);
    }

    return setIsModalOpen(false);
  };

  return (
    <ReactModal
      {...rest}
      ariaHideApp={ariaHideApp}
      css={{
        backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        flexFlow: 'column nowrap',
        outline: 'none',
        position: 'relative',
        width: '95vw',
        minWidth: '250px',
        [mqMin.tabletSmall]: {
          width: '70vw',
        },
      }}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
      isOpen={isModalOpen}
      onRequestClose={handleOnClose}
      style={{
        overlay: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          opacity: 1,
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          position: 'fixed',
          zIndex: theme.zIndices.z1500,
        },
      }}
    >
      <Box
        css={(theme: LucidTheme) => ({
          padding: theme.space.s8,
          paddingTop: 0,
          width: '100%',
          height: '100%',
          order: 1,
          [mqMin.tabletSmall]: {
            padding: theme.space.s24,
            paddingTop: 0,
          },
        })}
      >
        {children}
        {isModalOpen && (
          <Global
            styles={{
              '.ReactModal__Body--open': {
                overflowY: 'hidden',
              },
            }}
          />
        )}
        {videoLink && (
          <VideoPlayer
            controls
            description={videoDescription}
            descriptionElementId={videoDescriptionId}
            shouldAutoPlay={videoShouldAutoplay}
            url={videoLink}
          />
        )}
      </Box>
      <Box
        css={(theme: LucidTheme) => ({
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          padding: theme.space.s8,
        })}
      >
        <Button
          aria-label="Close modal"
          css={(theme: LucidTheme) => ({
            padding: theme.space.s8,
          })}
          onClick={handleOnClose}
          onKeyPress={handleOnClose}
          tabIndex={0}
          variant="basic"
        >
          <Icon color="coolGrey.100" type="X" />
        </Button>
      </Box>
    </ReactModal>
  );
};

export { Modal };
export default Modal;
